import { toNumber } from "@/features/account/api/mappers";
import type {
  AttachmentType,
  ChatAttachment,
  ChatConversation,
  ChatMessage,
  ChatMessagesPage,
  ChatParticipant,
  ChatReaction,
  ConversationsResult,
} from "@/features/chat/types";

import type {
  ChatAttachmentDto,
  ChatConversationDto,
  ChatMessageDto,
  ChatParticipantDto,
  ChatReactionDto,
  ConversationsDto,
  MessageDto,
  MessagesDto,
  UnreadDto,
  UploadDto,
} from "./dto";

const ATTACHMENT_TYPES: AttachmentType[] = [
  "image",
  "video",
  "audio",
  "document",
];

const count = (value: number | string | undefined) =>
  Math.max(0, Math.trunc(toNumber(value, 0)));

/**
 * Anything the backend doesn't recognise renders as a download row rather than
 * an inline player — `document` is the safe default, not a dropped attachment.
 */
function mapAttachmentType(value: string | undefined): AttachmentType {
  return ATTACHMENT_TYPES.includes(value as AttachmentType)
    ? (value as AttachmentType)
    : "document";
}

export function mapAttachment(dto: ChatAttachmentDto): ChatAttachment {
  const width = dto.width === undefined ? undefined : count(dto.width);
  const height = dto.height === undefined ? undefined : count(dto.height);
  return {
    type: mapAttachmentType(dto.type),
    url: dto.url ?? "",
    name: dto.name ?? "",
    mime: dto.mime ?? "",
    size: count(dto.size),
    ...(width === undefined ? {} : { width }),
    ...(height === undefined ? {} : { height }),
    compressed: Boolean(dto.compressed),
  };
}

/**
 * `other_user` is null when the counterpart account is gone. Callers render a
 * placeholder rather than crashing, so null passes through deliberately.
 */
export function mapParticipant(
  dto: ChatParticipantDto | null | undefined,
): ChatParticipant | null {
  if (!dto) return null;
  return {
    userId: String(dto.user_id ?? ""),
    username: dto.username ?? "",
    avatar: dto.avatar ?? "",
    lastActivity: dto.last_activity ?? null,
    online: Boolean(dto.online),
  };
}

export function mapReaction(dto: ChatReactionDto): ChatReaction {
  return {
    userId: String(dto.user_id ?? ""),
    emoji: dto.emoji ?? "",
  };
}

export function mapConversation(dto: ChatConversationDto): ChatConversation {
  return {
    id: String(dto.conversation_id ?? ""),
    lastMessage: dto.last_message ?? "",
    lastMessageAt: dto.last_message_at ?? null,
    lastSenderId: dto.last_sender_id ?? null,
    unreadCount: count(dto.unread_count),
    otherUser: mapParticipant(dto.other_user),
  };
}

export function mapConversations(dto: ConversationsDto): ConversationsResult {
  return {
    conversations: (dto.conversations ?? []).map(mapConversation),
    page: Math.max(1, Math.trunc(toNumber(dto.page, 1))),
  };
}

/**
 * A deleted message keeps its envelope (ids, timestamp) so the thread can render
 * a tombstone in place; text and attachments are already cleared server-side and
 * we don't re-populate them.
 */
export function mapMessage(dto: ChatMessageDto): ChatMessage {
  return {
    id: String(dto.message_id ?? ""),
    clientId: dto.client_id ?? null,
    conversationId: String(dto.conversation_id ?? ""),
    senderId: String(dto.sender_id ?? ""),
    recipientId: String(dto.recipient_id ?? ""),
    text: dto.text ?? "",
    attachments: (dto.attachments ?? []).map(mapAttachment),
    reactions: (dto.reactions ?? []).map(mapReaction),
    replyTo: dto.reply_to ?? null,
    read: Boolean(dto.read),
    readAt: dto.read_at ?? null,
    edited: Boolean(dto.edited),
    editedAt: dto.edited_at ?? null,
    deleted: Boolean(dto.deleted),
    createdAt: dto.created_at ?? "",
  };
}

export function mapMessagesPage(dto: MessagesDto): ChatMessagesPage {
  return {
    conversationId: String(dto.conversation_id ?? ""),
    otherUser: mapParticipant(dto.other_user),
    hasMore: Boolean(dto.has_more),
    messages: (dto.messages ?? []).map(mapMessage),
  };
}

/** Single-message endpoints (send / edit / delete / react) share one envelope. */
export function mapMessageEnvelope(dto: MessageDto): ChatMessage {
  return mapMessage(dto.message ?? {});
}

export function mapUnread(dto: UnreadDto): number {
  return count(dto.unread);
}

export function mapUpload(dto: UploadDto): ChatAttachment[] {
  return (dto.attachments ?? []).map(mapAttachment);
}
